import React, { useState, useEffect } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaCheckCircle } from 'react-icons/fa';
import { 
  FaChartLine, 
  FaWallet, 
  FaMoneyBillWave, 
  FaReceipt,
  FaQuestionCircle
} from 'react-icons/fa';

/**
 * Welcome Tour Modal Component
 * 
 * A guided tour that introduces new users to MnyMny's key features:
 * - Dashboard
 * - Wallet
 * - Cashflow
 * - Transactions
 * 
 * Shows automatically after registration and can be reopened via ? icon
 */
const WelcomeTourModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Reset to first step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const steps = [
    {
      title: 'Welcome to MnyMny!',
      description: 'Your personal financial management companion. Let\'s take a quick tour of what MnyMny can help you accomplish.',
      icon: <FaCheckCircle className="w-16 h-16 text-purple-500" />,
      image: null, // Placeholder for welcome graphic
      content: (
        <div className="space-y-4 text-center">
          <p className="text-lg text-gray-700">
            MnyMny helps you take control of your finances with powerful tools for tracking, budgeting, and planning.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">💰</div>
              <div className="text-sm font-medium text-gray-700 mt-2">Track Income</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">📊</div>
              <div className="text-sm font-medium text-gray-700 mt-2">Manage Budgets</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">💳</div>
              <div className="text-sm font-medium text-gray-700 mt-2">Monitor Spending</div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">🎯</div>
              <div className="text-sm font-medium text-gray-700 mt-2">Set Goals</div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Dashboard',
      description: 'Your financial command center. Get a complete overview of your financial health at a glance.',
      icon: <FaChartLine className="w-12 h-12 text-purple-500" />,
      image: null, // Placeholder for dashboard screenshot/graphic
      content: (
        <div className="space-y-4">
          {/* Demo Data Disclaimer */}
          <div className="text-center">
            <p className="text-xs text-gray-500 italic">
              * This is demo example data for illustration purposes only
            </p>
          </div>
          
          {/* Dashboard Illustration with Demo Data */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 border-2 border-purple-200 opacity-75">
            <div className="space-y-4">
              {/* Summary Cards Row with Demo Data */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Projected Net Monthly Income</div>
                  <div className="text-lg font-bold text-green-600">$4,250</div>
                  <div className="text-xs text-green-500 mt-1">+5.2%</div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Total Balance</div>
                  <div className="text-lg font-bold text-blue-600">$12,450</div>
                  <div className="text-xs text-blue-500 mt-1">+2.1%</div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Monthly Expenses</div>
                  <div className="text-lg font-bold text-red-600">$3,200</div>
                  <div className="text-xs text-gray-500 mt-1">-1.5%</div>
                </div>
              </div>
              
              {/* Chart Area - Real Bar Chart with Compelling Data */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-3">Spending Trends</div>
                <div className="flex items-end justify-between h-32 gap-2 px-2">
                  <div className="flex-1 flex flex-col items-center justify-end">
                    <div className="w-full bg-gradient-to-t from-purple-400 to-purple-300 rounded-t mb-1 transition-all" style={{ height: '35%', minHeight: '20px' }}></div>
                    <div className="text-xs text-gray-400 mt-1">Jan</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-end">
                    <div className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t mb-1 transition-all" style={{ height: '52%', minHeight: '20px' }}></div>
                    <div className="text-xs text-gray-400 mt-1">Feb</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-end">
                    <div className="w-full bg-gradient-to-t from-purple-600 to-purple-500 rounded-t mb-1 transition-all" style={{ height: '78%', minHeight: '20px' }}></div>
                    <div className="text-xs text-gray-400 mt-1">Mar</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-end">
                    <div className="w-full bg-gradient-to-t from-purple-700 to-purple-600 rounded-t mb-1 transition-all" style={{ height: '95%', minHeight: '20px' }}></div>
                    <div className="text-xs text-gray-400 mt-1">Apr</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-end">
                    <div className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t mb-1 transition-all" style={{ height: '68%', minHeight: '20px' }}></div>
                    <div className="text-xs text-gray-400 mt-1">May</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-end">
                    <div className="w-full bg-gradient-to-t from-purple-600 to-purple-500 rounded-t mb-1 transition-all" style={{ height: '82%', minHeight: '20px' }}></div>
                    <div className="text-xs text-gray-400 mt-1">Jun</div>
                  </div>
                </div>
              </div>
              
              {/* Category Breakdown Preview */}
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-3">Top Categories</div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded"></div>
                      <span className="text-sm text-gray-600">Food & Dining</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">$850</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded"></div>
                      <span className="text-sm text-gray-600">Transportation</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">$420</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-purple-500 rounded"></div>
                      <span className="text-sm text-gray-600">Shopping</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">$380</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">
            View your income, expenses, and account balances all in one place. Track trends and monitor your financial progress over time.
          </p>
        </div>
      )
    },
    {
      title: 'Wallet',
      description: 'Connect and manage all your bank accounts in one secure location.',
      icon: <FaWallet className="w-12 h-12 text-blue-500" />,
      image: null, // Placeholder for wallet screenshot/graphic
      content: (
        <div className="space-y-4">
          {/* Demo Data Disclaimer */}
          <div className="text-center">
            <p className="text-xs text-gray-500 italic">
              * This is demo example data for illustration purposes only
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200 opacity-75">
            <div className="space-y-3">
              {/* Account Card with Demo Data */}
              <div className="p-4 bg-white rounded-lg shadow-sm border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">Chase Checking</div>
                    <div className="text-xs text-gray-500">****4521</div>
                  </div>
                  <div className="text-lg font-bold text-gray-800">$8,240</div>
                </div>
              </div>
              {/* Account Card with Demo Data */}
              <div className="p-4 bg-white rounded-lg shadow-sm border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">Savings Account</div>
                    <div className="text-xs text-gray-500">****7890</div>
                  </div>
                  <div className="text-lg font-bold text-gray-800">$4,210</div>
                </div>
              </div>
              {/* Account Card with Demo Data */}
              <div className="p-4 bg-white rounded-lg shadow-sm border-l-4 border-purple-500">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">Credit Card</div>
                    <div className="text-xs text-gray-500">****3456</div>
                  </div>
                  <div className="text-lg font-bold text-red-600">-$1,250</div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">
            Securely link your bank accounts to automatically sync transactions and balances. Keep all your accounts organized and easily accessible.
          </p>
        </div>
      )
    },
    {
      title: 'Cash Flow',
      description: 'Track your income and expenses to understand where your money goes.',
      icon: <FaMoneyBillWave className="w-12 h-12 text-green-500" />,
      image: null, // Placeholder for cashflow screenshot/graphic
      content: (
        <div className="space-y-4">
          {/* Demo Data Disclaimer */}
          <div className="text-center">
            <p className="text-xs text-gray-500 italic">
              * This is demo example data for illustration purposes only
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-200 opacity-75">
            {/* Summary Cards with Demo Data */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                <div className="text-2xl font-bold text-green-600">$4,250</div>
                <div className="text-xs text-gray-600 mt-1">Monthly Income</div>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm text-center">
                <div className="text-2xl font-bold text-red-600">$3,200</div>
                <div className="text-xs text-gray-600 mt-1">Monthly Expenses</div>
              </div>
            </div>
            
            {/* Income Sources with Demo Data */}
            <div className="space-y-2 mb-4">
              <div className="text-xs font-medium text-gray-700 mb-2">Income Sources</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-white rounded">
                  <span className="text-sm text-gray-700">Salary</span>
                  <span className="text-sm font-medium text-green-600">$3,500</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded">
                  <span className="text-sm text-gray-700">Freelance</span>
                  <span className="text-sm font-medium text-green-600">$750</span>
                </div>
              </div>
            </div>
            
            {/* Expense Categories with Progress Bars */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700 mb-2">Expense Breakdown</div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Housing</span>
                      <span className="text-xs font-medium text-gray-700">$1,200</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: '37%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Food & Dining</span>
                      <span className="text-xs font-medium text-gray-700">$850</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '27%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Transportation</span>
                      <span className="text-xs font-medium text-gray-700">$420</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: '13%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">
            Monitor your income streams and categorize expenses to gain insights into your spending habits and financial patterns.
          </p>
        </div>
      )
    },
    {
      title: 'Transactions',
      description: 'View and manage all your financial transactions in one place.',
      icon: <FaReceipt className="w-12 h-12 text-orange-500" />,
      image: null, // Placeholder for transactions screenshot/graphic
      content: (
        <div className="space-y-4">
          {/* Demo Data Disclaimer */}
          <div className="text-center">
            <p className="text-xs text-gray-500 italic">
              * This is demo example data for illustration purposes only
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-6 border-2 border-orange-200 opacity-75">
            <div className="space-y-2">
              {/* Transaction Rows with Demo Data */}
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 text-sm">Starbucks Coffee</div>
                    <div className="text-xs text-gray-500">Food & Dining • Mar 15</div>
                  </div>
                  <div className="text-sm font-semibold text-red-600">-$5.45</div>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 text-sm">Amazon Purchase</div>
                    <div className="text-xs text-gray-500">Shopping • Mar 14</div>
                  </div>
                  <div className="text-sm font-semibold text-red-600">-$89.99</div>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm opacity-80">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 text-sm">Salary Deposit</div>
                    <div className="text-xs text-gray-500">Income • Mar 1</div>
                  </div>
                  <div className="text-sm font-semibold text-green-600">+$3,500.00</div>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm opacity-70">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 text-sm">Gas Station</div>
                    <div className="text-xs text-gray-500">Transportation • Feb 28</div>
                  </div>
                  <div className="text-sm font-semibold text-red-600">-$42.50</div>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm opacity-60">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 text-sm">Grocery Store</div>
                    <div className="text-xs text-gray-500">Food & Dining • Feb 27</div>
                  </div>
                  <div className="text-sm font-semibold text-red-600">-$125.30</div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">
            Review all your transactions, categorize them automatically, and search through your financial history with ease.
          </p>
        </div>
      )
    }
  ];

  const totalSteps = steps.length;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    // Mark tour as completed in localStorage
    localStorage.setItem('welcome-tour-completed', 'true');
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const handleSkip = () => {
    handleComplete();
  };

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-500 to-blue-500 p-6 text-white">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            aria-label="Close tour"
          >
            <FaTimes className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-4 mb-4">
            {currentStepData.icon}
            <div>
              <h2 className="text-2xl font-bold">{currentStepData.title}</h2>
              <p className="text-white/90 text-sm mt-1">{currentStepData.description}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-white/80 mb-2">
              <span>Step {currentStep + 1} of {totalSteps}</span>
              <span>{Math.round(((currentStep + 1) / totalSteps) * 100)}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2 transition-all duration-300"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 flex items-center justify-between gap-4">
          <button
            onClick={handleSkip}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium"
          >
            Skip Tour
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrevious}
              disabled={isFirstStep}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                isFirstStep
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FaChevronLeft className="inline mr-1" />
              Previous
            </button>

            <button
              onClick={handleNext}
              className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-blue-600 transition-all shadow-md"
            >
              {isLastStep ? (
                <>
                  Get Started
                  <FaCheckCircle className="inline ml-2" />
                </>
              ) : (
                <>
                  Next
                  <FaChevronRight className="inline ml-1" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTourModal;
